import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { GitBranch, Filter, Save, X, Plus } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { useToast } from "../hooks/use-toast";
import {
  useRepositoriesQuery,
  useWorkspaceQuery,
} from "../hooks/useRepositoryApi";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const SettingsPage = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [defaultBranch, setDefaultBranch] = useState(
    localStorage.getItem("defaultBranch") ||
      process.env.REACT_APP_DEFAULT_BRANCH ||
      ""
  );
  const [filteredProjects, setFilteredProjects] = useState(
    JSON.parse(localStorage.getItem("filteredProjects") || "[]")
  );
  const [projectInput, setProjectInput] = useState("");
  const [allProjects, setAllProjects] = useState([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState(
    localStorage.getItem("selectedWorkspace") ||
      process.env.REACT_APP_DEFAULT_WORKSPACE ||
      ""
  );

  // Fetch workspaces
  const { data: workspaces = [] } = useWorkspaceQuery();

  // Fetch repositories for the selected workspace to extract projects
  const { data: repositories = [] } = useRepositoriesQuery(selectedWorkspace, {
    enabled: !!selectedWorkspace,
  });

  // Extract unique projects from repositories when repositories change
  useEffect(() => {
    if (repositories && repositories.length > 0) {
      const projectsMap = new Map();

      repositories.forEach((repo) => {
        if (repo.project) {
          projectsMap.set(repo.project.key, {
            key: repo.project.key,
            name: repo.project.name,
            uuid: repo.project.uuid,
          });
        }
      });

      setAllProjects(Array.from(projectsMap.values()));
    }
  }, [repositories]);

  // Save settings to localStorage when they change
  useEffect(() => {
    localStorage.setItem("defaultBranch", defaultBranch);
  }, [defaultBranch]);

  useEffect(() => {
    localStorage.setItem("filteredProjects", JSON.stringify(filteredProjects));
  }, [filteredProjects]);

  useEffect(() => {
    localStorage.setItem("selectedWorkspace", selectedWorkspace);
  }, [selectedWorkspace]);

  const handleSaveSettings = () => {
    // Save all settings to localStorage
    localStorage.setItem("defaultBranch", defaultBranch);
    localStorage.setItem("filteredProjects", JSON.stringify(filteredProjects));
    localStorage.setItem("selectedWorkspace", selectedWorkspace);

    // Show success toast
    toast({
      title: "Settings saved",
      description: "Your settings have been saved successfully.",
    });
  };

  const handleAddProject = () => {
    if (projectInput.trim() !== "") {
      if (!filteredProjects.includes(projectInput.trim())) {
        setFilteredProjects([...filteredProjects, projectInput.trim()]);
      }
      setProjectInput("");
    }
  };

  const handleRemoveProject = (project) => {
    setFilteredProjects(filteredProjects.filter((p) => p !== project));
  };

  const handleWorkspaceChange = (workspace) => {
    setSelectedWorkspace(workspace);
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight mb-2">Settings</h1>
        <p className="text-muted-foreground">
          Configure your preferences for the Bitbucket Manager application.
        </p>
      </div>

      <div className="grid gap-6 mb-8">
        {/* Workspace Selection */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <CardTitle>Workspace</CardTitle>
            </div>
            <CardDescription>
              Select the default workspace to use across the application.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="workspace">Default Workspace</Label>
                <Select
                  value={selectedWorkspace}
                  onValueChange={handleWorkspaceChange}
                >
                  <SelectTrigger id="workspace">
                    <SelectValue placeholder="Select a workspace" />
                  </SelectTrigger>
                  <SelectContent>
                    {workspaces.map((workspace) => (
                      <SelectItem key={workspace.slug} value={workspace.slug}>
                        {workspace.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Default Branch Settings */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10 text-primary">
                <GitBranch className="h-5 w-5" />
              </div>
              <CardTitle>Default Branch</CardTitle>
            </div>
            <CardDescription>
              Set the default branch to use when running pipelines.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="defaultBranch">Branch Name</Label>
                <div className="flex gap-2">
                  <Input
                    id="defaultBranch"
                    placeholder="e.g., main, master, develop"
                    value={defaultBranch}
                    onChange={(e) => setDefaultBranch(e.target.value)}
                  />
                </div>
                <p className="text-sm text-muted-foreground">
                  This branch will be automatically selected when running
                  pipelines.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Project Filters */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10 text-primary">
                <Filter className="h-5 w-5" />
              </div>
              <CardTitle>Project Filters</CardTitle>
            </div>
            <CardDescription>
              Add projects that should be excluded from repository lists.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="projectFilter">Project Key</Label>
                <div className="flex gap-2">
                  <Input
                    id="projectFilter"
                    placeholder="Enter project key (e.g., PROJ)"
                    value={projectInput}
                    onChange={(e) => setProjectInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddProject();
                      }
                    }}
                  />
                  <Button onClick={handleAddProject}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add
                  </Button>
                </div>

                {allProjects.length > 0 && (
                  <div className="mt-4">
                    <Label className="mb-2 block">Available Projects</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {allProjects.map((project) => (
                        <Badge
                          key={project.key}
                          variant={
                            filteredProjects.includes(project.key)
                              ? "secondary"
                              : "outline"
                          }
                          className="cursor-pointer"
                          onClick={() => {
                            if (filteredProjects.includes(project.key)) {
                              handleRemoveProject(project.key);
                            } else {
                              setFilteredProjects([
                                ...filteredProjects,
                                project.key,
                              ]);
                            }
                          }}
                        >
                          {project.name} ({project.key})
                          {filteredProjects.includes(project.key) ? (
                            <X className="ml-1 h-3 w-3 inline" />
                          ) : (
                            <Plus className="ml-1 h-3 w-3 inline" />
                          )}
                        </Badge>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      Click a project to add or remove it from the filter list
                    </p>
                  </div>
                )}
              </div>

              {filteredProjects.length > 0 && (
                <div>
                  <Label className="mb-2 block">Filtered Projects</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {filteredProjects.map((project) => (
                      <Badge
                        key={project}
                        variant="secondary"
                        className="flex items-center"
                      >
                        {project}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-4 w-4 ml-1 p-0"
                          onClick={() => handleRemoveProject(project)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </Badge>
                    ))}
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">
                    Repositories from these projects will be hidden in
                    repository lists.
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => navigate(-1)}>
          Cancel
        </Button>
        <Button onClick={handleSaveSettings}>
          <Save className="mr-2 h-4 w-4" />
          Save Settings
        </Button>
      </div>
    </div>
  );
};

export default SettingsPage;
